import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { usePostDocumentsMutation } from "src/core/api/documents";
import { MappedStakeholder } from "src/core/constants/folder";
import { useFolder } from "src/core/contexts/FolderContext";
import {
  defaultDocumentValues,
  DocumentFormValues,
  documentSchema,
} from "src/core/forms/stakeholder/document.schema";
import { useCreateOrReplaceExternalDocumentMutation } from "src/core/gql/graphql";
import { useRequiredDocuments } from "src/core/hooks/common/useRequiredDocuments";
import { useUploadDocumentsErrorToast } from "src/core/hooks/folder/useUploadDocumentsErrorToast";
import { useToast } from "src/core/hooks/use-toast";
import { getDefaultDocuments } from "src/core/mappers/document.mapper";
import { uploadDocuments } from "src/core/services/document.service";
import { DocumentField } from "src/core/utils/documents";

export const useStakeholderDocument = (
  stakeholder: MappedStakeholder,
  documentToUpload: DocumentField,
  setOpen: (open: boolean) => void
) => {
  const { folderFile, refetch } = useFolder();
  const { t } = useTranslation();
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [postDocuments] = usePostDocumentsMutation();
  const [createOrReplaceExternalDocument] = useCreateOrReplaceExternalDocumentMutation();

  const requiredDocuments = useRequiredDocuments(folderFile, stakeholder.id);
  const { handleUploadErrors } = useUploadDocumentsErrorToast({ requiredDocuments, toast });

  const form = useForm<DocumentFormValues>({
    resolver: zodResolver(documentSchema(t)),
    defaultValues: defaultDocumentValues(),
  });

  const documentCode = documentToUpload.code;
  const documentLabel = documentToUpload.label;

  useEffect(() => {
    const defaultDoc = getDefaultDocuments(folderFile, stakeholder.id, [
      { code: documentCode, label: documentLabel },
    ]);
    form.reset({
      documents: { [documentCode]: defaultDoc[documentCode] },
    });
  }, [folderFile, stakeholder.id, documentCode, documentLabel, form]);

  /**
   * Réinitialise les champs du formulaire.
   * On utilise pas form.reset() car il y a un comportement inattendu lors de la réinitialisation du formulaire
   * (le champ se vide, puis se remplit avec un fichier vide).
   */
  const resetFormField = () => {
    const filePath = `documents.${documentCode}.file` as const;
    const existingPath = `documents.${documentCode}.existing` as const;

    form.setValue(filePath, null, { shouldDirty: true, shouldValidate: true });
    form.setValue(existingPath, false, { shouldDirty: true, shouldValidate: false });

    form.setValue(`documents.${documentCode}.expirationDate`, undefined, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleSubmit = async (data: DocumentFormValues) => {
    try {
      setIsLoading(true);
      const uploadResults = await uploadDocuments(
        data.documents,
        stakeholder.id,
        postDocuments,
        createOrReplaceExternalDocument
      );

      const failedUploads = uploadResults.filter((r) => !r.ok);
      const hasUploadErrors = handleUploadErrors(failedUploads);
      if (hasUploadErrors) {
        return;
      }

      await refetch();
    } catch (error) {
      console.error("useStakeholderDocument::handleSubmit", error);
    } finally {
      setIsLoading(false);
      resetFormField();
      setOpen(false);
    }
  };

  return { form, handleSubmit, isLoading };
};
